import React, {useEffect, useMemo, useState} from 'react'
import {GQL_SearchType} from '../../generated/graphql'
import {Box} from '../../webui/Box'
import {Typography} from '../../webui/Typography'
import {useAllRentals} from './gql/useAllRentals'
import {RentalsTable} from './RentalsTable'
import {Select, MenuItem} from '@mui/material'

interface Props {
  searchTerm?: string
  title?: string
}

const defaultMaxRows = 1000

export const AllRentals: React.FC<Props> = ({searchTerm, title}) => {
  const [paginationLimit, setPaginationLimit] = useState<number>(defaultMaxRows)

  // Memoize variables so we can call refetch(variablesMemo) reliably
  const variablesMemo = useMemo(
    () => ({
      pagination: {
        offset: 0, // start from the first page when limit/search changes
        limit: paginationLimit,
      },
      search: searchTerm
        ? {
            box: {query: searchTerm, type: GQL_SearchType.Contains},
            product: {query: searchTerm, type: GQL_SearchType.Contains},
            rentByUser: {query: searchTerm, type: GQL_SearchType.Contains},
          }
        : undefined,
    }),
    [paginationLimit, searchTerm],
  )

  const {
    data: {rentals, totalCount},
    loading,
    error,
    fetchMore,
    refetch,
  } = useAllRentals({
    variables: variablesMemo,
    // Ensure a network request happens when variables change
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
  })

  // Auto-refetch when paginationLimit or searchTerm changes
  useEffect(() => {
    // Guard: refetch may be undefined in some custom hooks; if present, use it.
    if (typeof refetch === 'function') {
      refetch(variablesMemo)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationLimit, searchTerm]) // intentionally omit variablesMemo to avoid double-calls

  const onFetchMoreClicked = async () => {
    await fetchMore({
      variables: {
        pagination: {
          offset: rentals.length,
          limit: paginationLimit,
        },
      },
    })
  }

  const hasReachedPenultimatePage = async () => {
    await onFetchMoreClicked()
  }

  return (
    <Box>
      {/* Key forces table state (e.g., internal pagination/virtualization) to reset on size change */}
      <RentalsTable
        key={`${paginationLimit}-${searchTerm ?? ''}`}
        error={error}
        hasReachedPenultimatePage={hasReachedPenultimatePage}
        loading={loading}
        rentals={rentals}
        searchEnabled={false}
        title={title}
      />

      <Box display="flex" justifyContent="space-between" alignItems="center" px={2} pb={1}>
        {/* Pagination limit selector — changing it triggers the useEffect above */}
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="body1">Max rows:</Typography>
          <Select size="small" value={paginationLimit} onChange={(e) => setPaginationLimit(parseInt((e.target as HTMLInputElement).value, 10))}>
            {[500, 1000, 2000, 5000].map((limit) => (
              <MenuItem key={limit} value={limit}>
                {limit}
              </MenuItem>
            ))}
          </Select>
        </Box>

        <Typography variant="h6">Total: {totalCount}</Typography>
      </Box>
    </Box>
  )
}
